/**
 * Welcome screen — shown when user is not logged in.
 * Nostr login via NIP-07 extension.
 */

import { useNostrUser } from "@/hooks/useNostr";
import { useState } from "react";
import { Sword, Shield, Trophy } from "lucide-react";

export function WelcomeScreen() {
  const { login } = useNostrUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLoginExtension = async () => {
    setLoading(true);
    setError(null);
    try {
      await login({ type: "extension" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to login with extension");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      {/* Hero */}
      <div className="mb-12 text-center">
        <div className="mb-6 flex items-center justify-center gap-3">
          <div className="rounded-2xl bg-primary/10 p-4">
            <Sword className="h-12 w-12 text-primary" />
          </div>
        </div>
        <h1 className="mb-3 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
          One Group to Rule Them All
        </h1>
        <p className="mx-auto max-w-lg text-lg text-muted-foreground">
          Form your team. Challenge rivals. Prove your dominance.
          <br />
          Nostr-powered team competition.
        </p>
      </div>

      {/* Features */}
      <div className="mb-10 grid max-w-2xl gap-6 sm:grid-cols-3">
        <div className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-6 text-center">
          <Shield className="h-8 w-8 text-primary" />
          <h3 className="font-semibold text-foreground">Form Teams</h3>
          <p className="text-sm text-muted-foreground">
            Create private communities with Concord
          </p>
        </div>
        <div className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-6 text-center">
          <Sword className="h-8 w-8 text-primary" />
          <h3 className="font-semibold text-foreground">Compete</h3>
          <p className="text-sm text-muted-foreground">
            Matchmake and play team-vs-team games
          </p>
        </div>
        <div className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-6 text-center">
          <Trophy className="h-8 w-8 text-primary" />
          <h3 className="font-semibold text-foreground">Climb Ranks</h3>
          <p className="text-sm text-muted-foreground">
            ELO ratings and leaderboards
          </p>
        </div>
      </div>

      {/* Login */}
      <div className="w-full max-w-sm">
        {error && (
          <div className="mb-4 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}
        <button
          onClick={handleLoginExtension}
          disabled={loading}
          className="w-full rounded-xl bg-primary px-6 py-4 text-base font-semibold text-primary-foreground transition-all hover:opacity-90 disabled:opacity-50"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
              Connecting...
            </span>
          ) : (
            "Sign in with Nostr"
          )}
        </button>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Requires a NIP-07 browser extension (nos2x, Alby, Flamingo, etc.)
        </p>
      </div>

      <p className="mt-16 text-xs text-muted-foreground">
        <a href="https://shakespeare.diy" target="_blank" rel="noopener" className="underline hover:text-foreground">
          Vibed with Shakespeare
        </a>
      </p>
    </div>
  );
}